import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Cpu, AlertTriangle, Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import { Panel, LoadingCards, LoadingPanel, EmptyState, MachineBadge, SortTh, useTableSort } from "@/components/widgets";
import { useFilter } from "@/context/FilterContext";
import { useMasterList } from "@/lib/useMasters";
import { useAuth } from "@/context/AuthContext";
import client, { formatApiError } from "@/lib/api";
import { fmtKg, fmtInt, fmtPct, fmtDate, monthLabel } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const MACHINE_COLORS = { L1: "#7C3AED", L2: "#0D9488", L3: "#D97706", UNALLOCATED: "#94A3B8" };
const colorFor = (code) => MACHINE_COLORS[code] || "#2563EB";

function QuickAssign({ record, machines, onDone }) {
  const [busy, setBusy] = useState(false);
  const assign = async (machineId) => {
    setBusy(true);
    try {
      await client.patch(`/nesting-records/${record.id}/machine`, {
        machineId, reason: "Allocated during planning", expectedVersion: record.version,
      });
      toast.success("Machine assigned");
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "VERSION_CONFLICT") toast.error("Record changed by someone else — refreshing.");
      else toast.error(formatApiError(d));
      onDone();
    } finally {
      setBusy(false);
    }
  };
  const allocatable = (machines || []).filter((m) => m.code !== "UNALLOCATED");
  return (
    <Select onValueChange={assign} disabled={busy}>
      <SelectTrigger className="h-8 w-28 text-xs" data-testid={`assign-${record.id}`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <SelectValue placeholder="Assign…" />}
      </SelectTrigger>
      <SelectContent>
        {allocatable.map((m) => <SelectItem key={m.id} value={m.id}>{m.display_name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Operations() {
  const { query, reset, update } = useFilter();
  const { can } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: machines } = useMasterList("machines");

  const { data, isLoading } = useQuery({
    queryKey: ["ops", query],
    queryFn: () => client.get(`/analytics/operations?${query}`).then((r) => r.data.data),
  });

  const empty = data && data.machineCards.length === 0;
  const refresh = () => qc.invalidateQueries();

  const excSort = useTableSort(data?.unallocatedExceptions, "nestingDate", "desc", {
    nestingDate: (r) => r.nestingDate, jobReference: (r) => r.jobReference,
    customer: (r) => r.customer.displayName, grade: (r) => r.materialGrade.displayName, weightKg: (r) => r.weightKg,
  });

  const drillTo = (period, machineId) => {
    if (!period) return;
    let from = period, to = period;
    if (data.throughputGranularity === "month") {
      const [y, m] = period.split("-").map(Number);
      from = `${period}-01`;
      to = `${period}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
    }
    update({ from, to, preset: "custom", machineIds: machineId ? [machineId] : [] });
    navigate("/records");
  };

  return (
    <Layout title="Operations Dashboard" subtitle="Machine throughput, planning workload & exceptions"
      meta={data ? `${data.unallocatedExceptions.length} unallocated` : null}>
      {isLoading ? (
        <div className="space-y-4"><LoadingCards n={4} /><LoadingPanel /></div>
      ) : empty ? (
        <Panel><EmptyState onReset={reset} /></Panel>
      ) : (
        <div className="space-y-5 animate-fade-up">
          {/* Machine cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.machineCards.map((m) => (
              <div key={m.machineId} className="panel p-4" data-testid={`machine-card-${m.code}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Cpu size={16} className="text-slate-400" />
                    <MachineBadge code={m.code} />
                  </div>
                  {m.code !== "UNALLOCATED" && (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 led-pulse" /> LIVE
                    </span>
                  )}
                </div>
                <p className="kpi-value text-2xl text-slate-900">{fmtKg(m.weightKg)}<span className="text-sm text-slate-400 ml-1">kg</span></p>
                <p className="text-xs text-slate-500">{fmtPct(m.sharePercent)} of filtered weight</p>
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t text-center" style={{ borderColor: "#F1F5F9" }}>
                  <div><p className="font-mono text-sm font-semibold">{fmtInt(m.recordCount)}</p><p className="text-[10px] text-slate-400">Nests</p></div>
                  <div><p className="font-mono text-sm font-semibold">{fmtKg(m.avgWeightKg)}</p><p className="text-[10px] text-slate-400">Avg kg</p></div>
                  <div><p className="font-mono text-sm font-semibold">{fmtInt(m.customerCount)}</p><p className="text-[10px] text-slate-400">Cust.</p></div>
                </div>
              </div>
            ))}
          </div>

          {/* Processed weight throughput split by machine (monthly, daily within a single month) */}
          <Panel title={data.throughputGranularity === "day" ? "Daily Processed Weight by Machine" : "Monthly Processed Weight by Machine"} testid="panel-throughput">
            <p className="text-xs text-slate-400 -mt-2 mb-3">Click any bar to open that period's records for the machine.</p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.throughput} barGap={2} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="period"
                  tickFormatter={(v) => (data.throughputGranularity === "day" ? v.slice(8, 10) : monthLabel(v))}
                  tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false}
                  interval={data.throughputGranularity === "day" ? 1 : 0} minTickGap={4} />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}t`} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, name) => [`${fmtKg(v)} kg`, name]}
                  labelFormatter={(v) => (data.throughputGranularity === "day" ? fmtDate(v) : monthLabel(v))}
                  contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                {(data.throughputMachines || []).map((mk) => (
                  <Bar key={mk.code} dataKey={mk.code} name={mk.code === "UNALLOCATED" ? "Unalloc" : mk.code}
                    fill={colorFor(mk.code)} radius={[3, 3, 0, 0]} maxBarSize={data.throughputGranularity === "day" ? 10 : 34}
                    cursor="pointer" onClick={(d) => drillTo(d?.payload?.period, mk.machineId)} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Programmer workload */}
            <Panel title="Programmer Workload" testid="panel-programmer">
              <div className="space-y-3">
                {data.programmerWorkload.map((p) => {
                  const max = data.programmerWorkload[0]?.weightKg || 1;
                  return (
                    <div key={p.id} data-testid={`programmer-${p.id}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-700">{p.displayName}</span>
                        <span className="font-mono text-xs text-slate-500">{fmtKg(p.weightKg)} kg · {p.recordCount} nests</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(p.weightKg / max) * 100}%`, background: "#7C3AED" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>

            {/* Grade demand */}
            <Panel title="Grade Demand Signals" testid="panel-grade-demand">
              <div className="space-y-2">
                {data.gradeDemand.map((g, i) => {
                  const max = data.gradeDemand[0]?.weightKg || 1;
                  return (
                    <div key={g.id} className="flex items-center gap-2 text-sm" data-testid={`grade-demand-${i}`}>
                      <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 shrink-0">{g.displayName}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(g.weightKg / max) * 100}%`, background: "#D97706" }} />
                      </div>
                      <span className="font-mono text-xs text-slate-500 w-20 text-right">{fmtKg(g.weightKg)}</span>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>

          {/* Unallocated exceptions */}
          <Panel title="Operational Exceptions — Unallocated Work" testid="panel-exceptions">
            {data.unallocatedExceptions.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No unallocated records. All work is assigned to a machine.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b" style={{ borderColor: "#E2E8F0" }}>
                      <SortTh label="Date" k="nestingDate" sort={excSort.sort} dir={excSort.dir} onSort={excSort.onSort} />
                      <SortTh label="Job Ref" k="jobReference" sort={excSort.sort} dir={excSort.dir} onSort={excSort.onSort} />
                      <SortTh label="Customer" k="customer" sort={excSort.sort} dir={excSort.dir} onSort={excSort.onSort} />
                      <SortTh label="Grade" k="grade" sort={excSort.sort} dir={excSort.dir} onSort={excSort.onSort} />
                      <SortTh label="Weight" k="weightKg" sort={excSort.sort} dir={excSort.dir} onSort={excSort.onSort} right />
                      {can("machine.assign") && <th className="py-2 px-3 font-medium">Assign</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {excSort.sorted.slice(0, 20).map((r) => (
                      <tr key={r.id} className="border-b hover:bg-amber-50/40" style={{ borderColor: "#F1F5F9" }} data-testid={`exception-row-${r.id}`}>
                        <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{fmtDate(r.nestingDate)}</td>
                        <td className="py-2 px-3 font-mono text-xs">{r.jobReference}</td>
                        <td className="py-2 px-3 truncate max-w-[160px]">{r.customer.displayName}</td>
                        <td className="py-2 px-3 text-xs text-slate-500">{r.materialGrade.displayName}</td>
                        <td className="py-2 px-3 font-mono text-right whitespace-nowrap">{fmtKg(r.weightKg)} kg</td>
                        {can("machine.assign") && (
                          <td className="py-2 px-3"><QuickAssign record={r} machines={machines} onDone={refresh} /></td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.unallocatedExceptions.length > 20 && (
                  <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                    <AlertTriangle size={12} /> Showing 20 of {data.unallocatedExceptions.length} unallocated records.
                  </p>
                )}
              </div>
            )}
          </Panel>
        </div>
      )}
    </Layout>
  );
}
